import React, { useState } from 'react';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';
import { GalleryItem } from '../../data/mock';
import { cn } from '../../lib/utils';

interface GalleryProps {
  images: GalleryItem[];
  category?: string;
  title?: string;
}

export const Gallery = ({ images, category, title }: GalleryProps) => {
  const [selectedImage, setSelectedImage] = useState<number | null>(null);

  const filteredImages = category 
    ? images.filter(img => img.category === category)
    : images;

  const openLightbox = (index: number) => {
    setSelectedImage(index);
    document.body.style.overflow = 'hidden';
  };

  const closeLightbox = () => {
    setSelectedImage(null);
    document.body.style.overflow = 'unset';
  };

  const nextImage = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (selectedImage !== null) {
      setSelectedImage((selectedImage + 1) % filteredImages.length);
    }
  };

  const prevImage = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (selectedImage !== null) {
      setSelectedImage((selectedImage - 1 + filteredImages.length) % filteredImages.length);
    }
  };

  if (filteredImages.length === 0) return null;

  return (
    <div className="py-12">
      {title && (
        <div className="text-center mb-12">
          <h3 className="text-3xl font-bold text-gray-900 mb-4">{title}</h3>
          <div className="w-24 h-1 bg-primary mx-auto rounded-full"></div>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
        {filteredImages.map((item, index) => (
          <div 
            key={item.id}
            className="group relative overflow-hidden rounded-xl cursor-pointer bg-gray-200 h-80 sm:h-64 md:h-72 lg:h-80"
            onClick={() => openLightbox(index)}
          >
            <img 
              src={item.image} 
              alt={item.title}
              className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
            />
            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors duration-300 flex items-center justify-center">
              <div className="text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300 transform translate-y-4 group-hover:translate-y-0 font-medium text-center px-4">
                {item.title}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Lightbox */}
      {selectedImage !== null && (
        <div 
          className="fixed inset-0 z-[60] bg-black/95 flex items-center justify-center p-4"
          onClick={closeLightbox}
        >
          <button 
            className="absolute top-4 right-4 text-white/70 hover:text-white p-2 z-50"
            onClick={closeLightbox}
          >
            <X size={32} />
          </button>

          <button 
            className="absolute left-4 top-1/2 -translate-y-1/2 text-white/70 hover:text-white p-2 hidden sm:block"
            onClick={prevImage}
          >
            <ChevronLeft size={48} />
          </button>

          <div className="relative max-w-5xl max-h-[90vh] w-full h-full flex items-center justify-center">
            <img 
              src={filteredImages[selectedImage].image} 
              alt={filteredImages[selectedImage].title}
              className="max-w-full max-h-full object-contain rounded-md shadow-2xl"
            />
            <div className="absolute bottom-4 left-0 right-0 text-center text-white text-lg font-medium bg-black/50 py-2 rounded-b-md backdrop-blur-sm">
              {filteredImages[selectedImage].title}
            </div>
          </div>

          <button 
            className="absolute right-4 top-1/2 -translate-y-1/2 text-white/70 hover:text-white p-2 hidden sm:block"
            onClick={nextImage}
          >
            <ChevronRight size={48} />
          </button>
        </div>
      )}
    </div>
  );
};
